import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useGrowthBook } from '@growthbook/growthbook-react'

import {
  setIsCookiesUser,
  setIsPersonalDataAllowedAction,
} from 'root-redux/actions/user'
import { selectLanguage } from 'root-redux/selects/common'
import {
  selectUserConfigData,
  selectUserCountryCode,
  selectUserStateCode,
} from 'root-redux/selects/user'

import {
  CookieConsentOption,
  Country,
  EU_COUNTRY_CODES,
  LANGUAGE_WITH_COOKIES,
  Locale,
  USA_STATES_WITH_COOKIES,
} from 'root-constants'

export const useCookieConsentAnswer = () => {
  const dispatch = useDispatch()
  const userCountryCode = useSelector(selectUserCountryCode)
  const userStateCode = useSelector(selectUserStateCode)
  const config = useSelector(selectUserConfigData)
  const growthBook = useGrowthBook()
  const lang = useSelector(selectLanguage)

  const isEUUser = EU_COUNTRY_CODES.includes(userCountryCode.toUpperCase())
  const isUSAUser = userCountryCode.toUpperCase() === Country.USA

  const { userCookieConsentAnswer } = useMemo(() => {
    return {
      userCookieConsentAnswer: config?.cookieConsent as string[],
    }
  }, [config])

  const hasAdvertizingAndMarketingCookie = useMemo(() => {
    return userCookieConsentAnswer?.includes(
      CookieConsentOption.ADVERTIZING_AND_TARGETING,
    )
  }, [userCookieConsentAnswer])

  const hasFunctionalCookie = useMemo(() => {
    return userCookieConsentAnswer?.includes(
      CookieConsentOption.FUNCTIONAL_COOKIES,
    )
  }, [userCookieConsentAnswer])

  const hasAnalyticsAndPerformanceCookie = useMemo(() => {
    return userCookieConsentAnswer?.includes(
      CookieConsentOption.ANALYTICS_AND_PERFORMANCE,
    )
  }, [userCookieConsentAnswer])

  const isCookiesUser = useMemo(() => {
    if (!LANGUAGE_WITH_COOKIES.includes(lang as Locale)) return false
    if (!growthBook?.ready) return true
    return (
      isEUUser || (isUSAUser && USA_STATES_WITH_COOKIES.includes(userStateCode))
    )
  }, [isEUUser, userStateCode, growthBook, isUSAUser, lang])

  const isPersonalDataAllowed =
    (isCookiesUser && hasFunctionalCookie) || !isCookiesUser

  useEffect(() => {
    dispatch(setIsPersonalDataAllowedAction(isPersonalDataAllowed))
  }, [dispatch, isPersonalDataAllowed])

  useEffect(() => {
    dispatch(setIsCookiesUser(isCookiesUser))
  }, [dispatch, isCookiesUser])

  return {
    isEUUser,
    hasAdvertizingAndMarketingCookie,
    hasFunctionalCookie,
    hasAnalyticsAndPerformanceCookie,
    userCookieConsentAnswer,
    isPersonalDataAllowed,
    isCookiesUser,
    isUSAUser,
  }
}
